import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface Faq {
  question: string;
  answer: string;
  icon: string;
}

const DEFAULT_FAQS: Faq[] = [
  {
    question: "What is HovaStore POS?",
    answer:
      "HovaStore POS is a tax-compliant point-of-sale system for African SMEs, with EBM, inventory, sales & analytics features.",
    icon: "💼",
  },
  {
    question: "Is HovaStore certified by RRA?",
    answer:
      "Yes, it is VSDC CIS certified and fully integrated with Rwanda Revenue Authority’s EBM 2.1.",
    icon: "🧾",
  },
  {
    question: "Can you migrate my data from my current RRA EBM?",
    answer:
      "Yes! When you start using HovaStore POS, we help you migrate all your existing items and data from your current RRA EBM VSDC system to HovaStore. Our support team ensures a smooth transition so you don’t lose any information.",
    icon: "🛠️",
  },
  {
    question: "Can I try a free trial?",
    answer:
      "Yes. New users get a free trial with full access to all features. No credit card required.",
    icon: "🎁",
  },
  {
    question: "How do I create a new store?",
    answer:
      "Sign up using your phone number, enter the OTP sent via SMS, and follow the steps to create your store.",
    icon: "🏪",
  },
  {
    question: "How many stores can I manage?",
    answer:
      "You can manage multiple stores under one account and easily switch between them from your profile settings.",
    icon: "🔄",
  },
  {
    question: "How can I manage inventory?",
    answer:
      "Go to the Inventory section, where you can add products/services, record purchases, and set sale prices.",
    icon: "📦",
  },
  {
    question: "Can I issue refund receipts?",
    answer:
      "Yes, HovaStore supports generating refund receipts. Go to Transactions → Select receipt → Generate Refund.",
    icon: "🔁",
  },
  {
    question: "How do I contact support?",
    answer:
      "You can reach us via email at info@hova.ai or call +250 793 090 500 for technical assistance.",
    icon: "☎️",
  },
  {
    question: "Does HovaStore track expenses?",
    answer:
      "Yes, it includes expense tracking and summaries which you can access from the dashboard.",
    icon: "📊",
  },
  {
    question: "How do I print or share receipts?",
    answer:
      "After approving a transaction, you can print via connected printers or share the receipt PDF via messaging apps.",
    icon: "🖨️",
  },
];

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const { t } = useTranslation();

  return (
    <section
      className="relative overflow-hidden bg-white px-4 py-20 dark:bg-slate-950 sm:px-6"
      id="faqs"
    >
      <div className="absolute left-8 top-20 hidden h-28 w-28 rounded-full border border-primary/10 bg-primary/5 hs-soft-pulse lg:block" />
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-primary dark:text-pink-200">
            {t("faq.eyebrow")}
          </p>
          <h1 className="mt-3 text-3xl font-black text-slate-950 sm:text-5xl dark:text-white">
            {t("faq.title")}
          </h1>
          <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-300">
            {t("faq.description")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[0.72fr_1.28fr]">
          <div className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6 shadow-xl shadow-slate-900/5 dark:border-slate-800 dark:bg-slate-900">
            <div className="rounded-2xl bg-white p-5 shadow-sm dark:bg-slate-950">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-primary dark:text-pink-200">
                {t("faq.supportEyebrow")}
              </div>
              <h2 className="mt-3 text-2xl font-black text-slate-950 dark:text-white">
                {t("faq.supportTitle")}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {t("faq.supportDescription")}
              </p>
              <a
                href="#contact"
                className="mt-6 inline-flex rounded-xl bg-primary px-5 py-3 text-sm font-black text-white transition hover:-translate-y-0.5 hover:bg-[#6d1f55]"
              >
                {t("faq.contactSupport")}
              </a>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[
                t("contact.marqueeEbm"),
                t("contact.marqueeInventory"),
                t("contact.marqueeReceipts"),
                t("contact.marqueeReports"),
              ].map((item) => (
                <div
                  key={item}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-center text-sm font-black text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {DEFAULT_FAQS.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={faq.question}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:border-primary/30 dark:border-slate-800 dark:bg-slate-900"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    className="flex w-full items-center gap-4 px-5 py-4 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg dark:bg-pink-300/10">
                      {faq.icon}
                    </span>
                    <span className="flex-1 text-base font-black text-slate-950 dark:text-white">
                      {faq.question}
                    </span>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-primary transition dark:border-slate-700 ${
                        isOpen ? "rotate-45 bg-primary text-white" : ""
                      }`}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:px-20">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQPage;
